import {
  BuyBackPurchaseOrderPayload,
  CancelPurchaseOrderPayload,
  GetOptions,
  PurchaseOrder,
  QueryConstraint,
  UnlinkPurchaseOrderPayload,
} from "@/core";
import { repositoryHost } from "@/repositories";
import { serviceHost } from "@/services";
import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { getNextPageParam } from "../utils/page-params";
import { fetchAllPages } from "../utils/fetch-all-pages";

const databaseService = serviceHost.getDatabaseService();
const functionsService = serviceHost.getFunctionsService();
const purchaseOrderRepository =
  repositoryHost.getPurchaseOrderRepository(databaseService);

export const usePurchaseOrders = (options: GetOptions<PurchaseOrder>) => {
  return useInfiniteQuery({
    queryKey: ["purchase-orders", "get", JSON.stringify(options)],
    queryFn: ({ pageParam }) =>
      purchaseOrderRepository.getAll({
        ...options,
        pagination: {
          ...options.pagination,
          cursor: pageParam,
        },
      }),
    initialPageParam: "",
    getNextPageParam: getNextPageParam(options),
  });
};

const ALL_PURCHASE_ORDERS_PAGE_SIZE = 300;

/**
 * Loads ALL purchase orders by auto-paging through the infinite query. Lazy:
 * pass `enabled=false` until the data is actually needed (e.g. opening a match
 * picker) so the page doesn't fetch the whole table on mount. Cached under a
 * stable key, so reopening reuses what's already loaded. `isLoading` stays true
 * until every page is in.
 */
export const useAllPurchaseOrders = (enabled = true) => {
  const query = useInfiniteQuery({
    queryKey: ["purchase-orders", "all"],
    enabled,
    queryFn: ({ pageParam }) =>
      purchaseOrderRepository.getAll({
        orderBy: { field: "createdAt", direction: "desc" },
        pagination: {
          limit: ALL_PURCHASE_ORDERS_PAGE_SIZE,
          cursor: pageParam,
        },
      }),
    initialPageParam: "",
    getNextPageParam: getNextPageParam({
      pagination: { limit: ALL_PURCHASE_ORDERS_PAGE_SIZE },
    }),
  });

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  useEffect(() => {
    if (enabled && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [enabled, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const purchaseOrders = useMemo(
    () => query.data?.pages.flat() ?? [],
    [query.data],
  );

  return {
    purchaseOrders,
    isLoading: enabled && (query.isLoading || query.hasNextPage),
    isFetching: query.isFetchingNextPage,
  };
};

/**
 * Auto-pages every purchase order matching `queryConstraints`, newest first.
 *
 * Exports use this instead of the table's in-memory rows so the file covers the
 * whole selected range rather than just the first loaded page.
 */
export const fetchAllPurchaseOrders = (
  queryConstraints: QueryConstraint<keyof PurchaseOrder & string>[] = [],
): Promise<PurchaseOrder[]> =>
  fetchAllPages<PurchaseOrder>(
    (options) => purchaseOrderRepository.getAll(options),
    {
      queryConstraints,
      orderBy: { field: "createdAt", direction: "desc" },
    },
  );

/**
 * Fetches a specific set of purchase orders by id (deduped) and returns a map.
 * Shares the single-PO cache key with `usePurchaseOrderById`, so only POs not
 * already cached are fetched. Used to render referenced POs without loading the
 * whole table.
 */
export const usePurchaseOrdersByIds = (ids: string[]) => {
  const idsKey = Array.from(new Set(ids.filter(Boolean)))
    .sort()
    .join(",");
  const uniqueIds = useMemo(
    () => (idsKey ? idsKey.split(",") : []),
    [idsKey],
  );

  const queries = useQueries({
    queries: uniqueIds.map((id) => ({
      queryKey: ["purchase-orders", "get-by-id", id],
      queryFn: () => purchaseOrderRepository.get({ id }),
      enabled: id.length > 0,
    })),
  });

  const map = new Map<string, PurchaseOrder>();
  queries.forEach((query, index) => {
    if (query.data) {
      map.set(uniqueIds[index], query.data);
    }
  });
  return map;
};

export const usePurchaseOrderById = (poId: string) => {
  return useQuery({
    queryKey: ["purchase-orders", "get-by-id", poId],
    enabled: poId.length > 0,
    queryFn: () => purchaseOrderRepository.get({ id: poId }),
  });
};

export const usePurchaseOrdersByVendorId = (vendorId: string) => {
  return useQuery({
    queryKey: ["purchase-orders", "get-by-vendor-id", vendorId],
    enabled: vendorId.length > 0,
    queryFn: () =>
      purchaseOrderRepository.getAll({
        queryConstraints: [
          { field: "vendorId", operator: "==", value: vendorId },
        ],
        pagination: { limit: 500 },
        orderBy: { field: "createdAt", direction: "desc" },
      }),
  });
};

export const usePurchaseOrdersByInventoryId = (inventoryId: string) => {
  return useQuery({
    queryKey: ["purchase-orders", "get-by-inventory-id", inventoryId],
    enabled: inventoryId.length > 0,
    queryFn: () =>
      purchaseOrderRepository.getAll({
        queryConstraints: [
          { field: "inventoryId", operator: "==", value: inventoryId },
        ],
        pagination: { limit: 500 },
        orderBy: { field: "createdAt", direction: "desc" },
      }),
  });
};

export const useUpdatePurchaseOrderPaymentStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      poId: string;
      paymentStatus: "unpaid" | "partially_paid" | "paid";
    }) =>
      purchaseOrderRepository.update({
        id: payload.poId,
        data: {
          paymentStatus: payload.paymentStatus,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["purchase-orders"] });
      queryClient.invalidateQueries({ queryKey: ["payments"] });
      queryClient.invalidateQueries({ queryKey: ["audit-logs"] });
    },
  });
};

export const useUpdatePurchaseOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      poId: string;
      data: Partial<{
        status: "open" | "closed" | "blocked";
        paymentTiming: "pay_in_advance" | "pay_after" | "pay_once_booked";
        paymentStatus: "unpaid" | "partially_paid" | "paid";
        nextPaymentDate?: string;
        okToPay?: boolean;
        paymentMethod?: "paypal_goods_services" | "paypal_friends_family" | "paypal_goods_services_fees" | "paypal" | "bank" | "zelle" | "card" | "crypto" | "other";
        paymentNotes?: string;
        paymentDetails?: string;
        buyPrice?: number;
        taxCost?: number;
        amountBought?: number;
        notes?: string;
        sdrInfo?: string;
        sdrAgentId?: string;
      }>;
    }) =>
      functionsService.updatePurchaseOrder(payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["purchase-orders"] });
      queryClient.invalidateQueries({
        queryKey: ["purchase-orders", "get-by-id", variables.poId],
      });
      if (variables.data.status || variables.data.amountBought !== undefined) {
        queryClient.invalidateQueries({ queryKey: ["inventories"] });
      }
      queryClient.invalidateQueries({ queryKey: ["audit-logs"] });
    },
  });
};

export const useBuyBackPurchaseOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: BuyBackPurchaseOrderPayload) =>
      functionsService.buyBackPurchaseOrder(payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["purchase-orders"] });
      queryClient.invalidateQueries({
        queryKey: ["purchase-orders", "get-by-id", variables.poId],
      });
      queryClient.invalidateQueries({ queryKey: ["inventories"] });
      queryClient.invalidateQueries({ queryKey: ["payments"] });
      queryClient.invalidateQueries({ queryKey: ["audit-logs"] });
    },
  });
};

export const useCancelPurchaseOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CancelPurchaseOrderPayload) =>
      functionsService.cancelPurchaseOrder(payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["purchase-orders"] });
      queryClient.invalidateQueries({
        queryKey: ["purchase-orders", "get-by-id", variables.poId],
      });
      queryClient.invalidateQueries({ queryKey: ["inventories"] });
      // Outstanding purchase invoices on the PO are voided alongside it.
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["audit-logs"] });
    },
  });
};

export const useUnlinkPurchaseOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UnlinkPurchaseOrderPayload) =>
      functionsService.unlinkPurchaseOrder(payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["purchase-orders"] });
      queryClient.invalidateQueries({
        queryKey: ["purchase-orders", "get-by-id", variables.poId],
      });
      queryClient.invalidateQueries({ queryKey: ["inventories"] });
      queryClient.invalidateQueries({ queryKey: ["audit-logs"] });
    },
  });
};
